import "rxjs/add/operator/map";
import "rxjs/add/operator/catch";
import "rxjs/add/observable/throw";

import { Injectable } from "@angular/core";
import { Headers, Http, Jsonp, RequestOptions, Response } from "@angular/http";
import { Observable } from "rxjs/Observable";
import { Subscription } from "rxjs/Subscription";

import { environment } from "../../../environments/environment";
import { AuthBody } from "../model/authBody";
import { AuthResponse } from "../model/authResponse";
import { ErrorResponse } from "../model/errorResponse";
import { User } from "../model/user";
import { SessionService } from "./session.service";

const API_URL = environment.apiUrl;

@Injectable()
export class UsersService {
    constructor(private http: Http, private sessionService: SessionService) {}

    public getLoggedInUser(): Observable<User> {
        const headers = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });
        return this.http
            .get(API_URL + "/v1/users/me", headers)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public getUserById(id: string): Observable<User> {
        const headers = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });
        return this.http
            .get(API_URL + "/v1/users/" + id, headers)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public getLoggedInUserByToken(token: String) {
        const headers = new RequestOptions({
            headers: new Headers({ Authorization: "Bearer " + token }),
        });
        return this.http
            .get(API_URL + "/v1/users/me", headers)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public getAllUsers(filter = undefined): Observable<User[]> {
        const headers = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });

        return this.http
            .get(
                API_URL + "/v1/users" + (filter ? "?filter=" + filter : ""),
                headers,
            )
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public getUsersByOrganisationId(organisationId): Observable<User[]> {
        const headers = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });
        return this.http
            .get(
                API_URL + "/v1/organisations/" + organisationId + "/users",
                headers,
            )
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public createUserwithObject(
        user: User,
        organisationId: string,
    ): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        const body = {
            name: user.name,
            email: user.email,
            role: user.role,
            organisation: organisationId,
        };

        return this.http
            .post(API_URL + "/v1/users", JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public createAdmin(
        username: string,
        email: string,
        role: string,
        organisationId: string,
    ): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        const body = {
            name: username,
            email: email,
            role: role,
            organisation: organisationId,
        };

        return this.http
            .post(API_URL + "/v1/users", JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public createUser(
        username: string,
        email: string,
        role: string,
        organisationId: string,
        log_requests: boolean,
    ): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        let body;

        if (organisationId === null) {
            body = {
                name: username,
                email: email,
                role: role,
                log_requests: log_requests,
            };
        } else {
            body = {
                name: username,
                email: email,
                role: role,
                organisation: organisationId,
                log_requests: log_requests,
            };
        }

        return this.http
            .post(API_URL + "/v1/users", JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public resetPassword(email: string): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });

        let body = {
            email: email,
        };

        return this.http
            .post(
                API_URL + "/v1/users/reset-password",
                JSON.stringify(body),
                tempHeaders,
            )
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public resendLogin(email: string): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        let body = {
            email: email,
        };

        return this.http
            .post(
                API_URL + "/v1/users/resend-login-email",
                JSON.stringify(body),
                tempHeaders,
            )
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public updateUserRole(id: string, role: string): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        const body = { role: role };

        return this.http
            .put(API_URL + "/v1/users/" + id, JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public updateUserPassword(
        id: string,
        oldPassword: string,
        newPassword: string,
    ): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        const body = { old_password: oldPassword, password: newPassword };

        return this.http
            .put(API_URL + "/v1/users/" + id, JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public updateUser(id: string, user: User): Observable<User> {
        const tempHeaders = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "application/json",
            }),
        });
        const body = {
            name: user.name,
            email: user.email,
            role: user.role,
            organisation: user.organisation,
            log_requests: user.log_requests,
            "2fa": user["2fa"],
        };

        return this.http
            .put(API_URL + "/v1/users/" + id, JSON.stringify(body), tempHeaders)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public deleteUser(id: string): Observable<User> {
        const headers = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });

        return this.http
            .delete(API_URL + "/v1/users/" + id, headers)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public deleteUsers(ids: string[]): Observable<User> {
        const options = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
            body: {
                users: ids,
            },
        });

        return this.http
            .delete(API_URL + "/v1/users", options)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public deleteTrainingProgressByUser(id: string): Observable<User> {
        const options = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
        });

        return this.http
            .delete(`${API_URL}/v1/users/${id}/training.progress`, options)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    public deleteTrainingProgressByUsers(ids: string[]): Observable<User> {
        const options = new RequestOptions({
            headers: new Headers({
                Authorization: "Bearer " + localStorage.getItem("token"),
            }),
            body: {
                users: ids,
            },
        });

        return this.http
            .delete(`${API_URL}/v1/users/training.progress`, options)
            .map((response) => {
                return response.json();
            })
            .catch(this.handleError.bind(this));
    }

    private handleError(error: Response | any) {
        console.log(error);
        return this.sessionService.handleAuthenticatedRequestError(error);
    }
}



// WEBPACK FOOTER //
// /tmp/build_f0fbff5b/src/app/data/services/users.service.ts